import { format } from "date-fns";
import Handlebars from "handlebars";
import Papa from "papaparse";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateTimePicker from "../components/DateTimePicker";
import { Layout } from "../components/Layout";
import RichTextEditor from "../components/RichTextEditor";
import { EmailTemplate } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";
import { Toast } from "../services/toast-service";
type CreateScreenProps = {
  userInfo: UserInfo;
};

const CreateScreen: React.FC<CreateScreenProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(true);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [recipientsCSVFile, setRecipientsCSVFile] = useState<File | undefined>(
    undefined,
  );
  const [recipientsAll, setRecipientsAll] = useState(false);
  const [pushNotification, setPushNotification] = useState(false);
  const [emailNotification, setEmailNotification] = useState(false);
  const [mediumError, setMediumError] = useState(false);
  const [csvError, setCsvError] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();
  const [compiledEmailTemplate, setCompiledEmailTemplate] =
    useState<EmailTemplate>();
  const [emailSignature, setEmailSignature] = useState<string>("");

  const navigate = useNavigate();

  useEffect(() => {
    if (
      props.userInfo &&
      (props.userInfo.isCoordinator || props.userInfo.isEditor)
    ) {
      navigate("/academic-compose-email");
    }
  }, [props.userInfo, navigate]);

  useEffect(() => {
    setPushNotification(false);
    setEmailNotification(false);
  }, []);

  const sendAtAssignNow = () => {
    const sendAtformatted = format(new Date(), "yyyy-MM-dd HH:mm");
    setSendAt(sendAtformatted);
  };

  // fetch email templates
  const [emailTemplates, setEmailTemplates] = useState<{ name: string }[]>([]);
  useEffect(() => {
    const fetchEmailTemplates = async () => {
      setLoading(true);
      notificationService
        .getEmailTemplates()
        .then((templates) => {
          setEmailTemplates(templates);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    fetchEmailTemplates();
  }, []);

  useEffect(() => {
    compileEmailTemplate(emailTemplate);
  }, [title, body, link, emailSignature, emailTemplate]);

  const getEmailTemplate = (name: string) => {
    setLoading(true);
    notificationService
      .getEmailTemplate(name)
      .then((template) => {
        setEmailTemplate(template);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateCSVForEmails = (file: File) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const rows = results.data;
          const containsEmail = rows.some(
            (row: any) =>
              row.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email),
          );
          resolve(containsEmail);
        },
        error: (error) => reject(error),
      });
    });
  };

  const handleRecipientsCSVFileChange = async (e: {
    target: HTMLInputElement;
  }) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setRecipientsCSVFile(file);
      if (emailNotification) {
        const containsEmail = await validateCSVForEmails(file);
        setCsvError(!containsEmail);
      }
    } else {
      setRecipientsCSVFile(undefined);
      setCsvError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!loading) {
      try {
        if (body.length === 0 || sendAt.length === 0) {
          throw new Error("Not all required fields have been entered.");
        }
        if (link.length > 0 && !link.startsWith("http")) {
          throw new Error(
            "Ensure URL field is either blank or starts with http:// or https://",
          );
        }
        if (!recipientsAll) {
          if (!recipientsCSVFile) {
            throw new Error("Recipients have not been added.");
          }
          if (emailNotification && csvError) {
            throw new Error(
              "CSV file must contain at least one valid email address.",
            );
          }
        }
        if (!pushNotification && !emailNotification) {
          setMediumError(true);
          throw new Error("At least one delivery medium must be selected.");
        } else {
          setMediumError(false);
        }

        setLoading(true);
        await notificationService.createNotification({
          body,
          title,
          link,
          sendAt: new Date(sendAt).toISOString(),
          recipientsAll,
          recipientsCSV: recipientsCSVFile,
          pushNotification,
          emailNotification,
          emailTemplate: emailTemplate?.name,
          emailSignature,
        });
        Toast(
          "Success!!",
          "The new notification has been saved for review.",
          "success",
        );
        navigate("/edit");
      } catch (err: unknown) {
        console.error("Error saving notification:", err);

        let errorMessage = "Something went wrong saving notification.";

        if (notificationService.isHttpError(err)) {
          const httpError: any = err;
          if (httpError.response?.data?.message) {
            errorMessage = httpError.response.data.message;
          } else if (httpError.message) {
            errorMessage = httpError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        Toast("Error!!", errorMessage, "danger");
      }
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    const name = e.target.name;
    if (name === "title") {
      setTitle(value);
    } else if (name === "body") {
      setBody(value);
    } else if (name === "link") {
      setLink(value);
    }
  };

  const handleDateTimeInputChange = (value: string) => {
    setSendAt(value);
  };

  const handleAssignSendAtASAP = () => {
    setShowDatePicker(false);
    sendAtAssignNow();
  };

  const handleResetSendAt = () => {
    setShowDatePicker(true);
    setSendAt("");
  };

  const handleToggleRecipientsAll = () => {
    setRecipientsAll(!recipientsAll);
  };

  const handlePushNotificationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPushNotification(e.target.checked);
  };

  const handleEmailNotificationChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = e.target.checked;
    setEmailNotification(isChecked);
    if (isChecked && recipientsCSVFile) {
      const containsEmail = await validateCSVForEmails(recipientsCSVFile);
      setCsvError(!containsEmail);
    } else {
      setCsvError(false);
    }
  };

  const compileEmailTemplate = (template?: EmailTemplate) => {
    if (!template) {
      return;
    }
    const complied = Handlebars.compile(template.html)({
      EmailSignature: emailSignature,
      Link: link,
      Body: body,
    });
    setCompiledEmailTemplate({
      ...template,
      html: complied,
    });
  };
  const handleChangeEmailTemplate = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    if (e.target.value) {
      getEmailTemplate(e.target.value);
    } else {
      setEmailTemplate(undefined);
      setCompiledEmailTemplate(undefined);
    }
  };

  const handleChangeEmailSignature = (content: string) => {
    setEmailSignature(content);
  };

  return (
    <Layout userInfo={props.userInfo} loading={loading}>
      {
        <form onSubmit={handleSubmit} className="flex flex-col pt-8 px-20">
          <div className="flex flex-row">
            <div className="flex flex-1 flex-col mr-10">
              <div className="flex flex-col pb-4">
                <label htmlFor="title" className="text-sm pb-2">
                  Notification title - CSV ONLY: supported template keywords{" "}
                  <span style={{ fontStyle: "italic" }}>
                    {`{{unikey}} {{uos}}`}
                  </span>
                </label>
                <input
                  placeholder="Enter optional title"
                  type="text"
                  name="title"
                  value={title}
                  onChange={handleInputChange}
                  className="border px-2 py-1 text-sm"
                />
              </div>
              <div className="flex flex-col pb-4">
                <label htmlFor="body" className="text-sm pb-2">
                  Notification text <span className="text-red-500">*</span> -
                  CSV ONLY: supported template keywords{" "}
                  <span style={{ fontStyle: "italic" }}>
                    {`{{unikey}} {{uos}}`}
                  </span>
                </label>
                <textarea
                  placeholder="Enter notification text"
                  name="body"
                  value={body}
                  onChange={handleInputChange}
                  className="border px-2 py-1 text-sm"
                  required
                />
              </div>
              <div className="flex flex-col pb-4">
                <label htmlFor="title" className="text-sm pb-2">
                  URL redirect upon user interaction (leave blank for no
                  redirect)
                </label>
                <input
                  placeholder="eg, http://sydney.edu.au"
                  type="url"
                  name="link"
                  value={link}
                  onChange={handleInputChange}
                  className="border px-2 py-1 text-sm"
                />
              </div>
              <div className="flex flex-col pb-4">
                <label htmlFor="emailTemplate" className="text-sm pb-2">
                  Email template
                </label>
                <select
                  name="emailTemplate"
                  value={emailTemplate?.name}
                  onChange={handleChangeEmailTemplate}
                  className="border px-2 py-2 text-sm"
                >
                  <option value="">Select an email template</option>
                  {emailTemplates.map((template) => (
                    <option key={template.name} value={template.name}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              {emailTemplate && (
                <div className="flex flex-col pb-4">
                  <label className="text-sm pb-2">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={emailTemplate?.subject}
                    disabled={true}
                    className="border px-2 py-1 text-sm"
                  />
                </div>
              )}
              {emailTemplate && (
                <div className="flex flex-col pb-8">
                  <label className="text-sm pb-2">Email Signature</label>
                  <RichTextEditor
                    value={emailSignature}
                    onChange={handleChangeEmailSignature}
                  />
                </div>
              )}

              {compiledEmailTemplate && (
                <div className="flex flex-col pb-4">
                  <label className="text-sm pb-2">Email template preview</label>
                  <RichTextEditor
                    // email template is for view only and not editable
                    readOnly={true}
                    value={compiledEmailTemplate?.html || ""}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-1 flex-col ml-10">
              <div className="flex flex-col pb-4">
                <label htmlFor="sendat" className="text-sm pb-2">
                  Schedule date and time <span className="text-red-500">*</span>
                </label>
                <div>
                  {showDatePicker ? (
                    <div className="flex flex-row items-center">
                      <DateTimePicker
                        name="sendat"
                        value={sendAt}
                        onChange={handleDateTimeInputChange}
                        required
                        inputClassName="border px-2 py-1 text-sm"
                      />
                      <span className="mx-6">or</span>
                      <button
                        type="button"
                        className="bg-neutral-600 text-white text-xs rounded px-5 py-2"
                        onClick={handleAssignSendAtASAP}
                      >
                        ASAP
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-row items-center">
                      <span className="text-xl font-semibold text-[#e64626]">
                        ASAP
                      </span>
                      <button
                        type="button"
                        className="bg-neutral-600 text-white text-xs rounded px-5 py-2 mx-6"
                        onClick={handleResetSendAt}
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p>
                Your notification will be sent at the scheduled date and time
                pending approval by an appropriate administrator.
              </p>
            </div>
          </div>
          <div className="flex flex-row mt-8">
            <div className="flex flex-1 flex-row items-center justify-center">
              <h3>Recipients: {recipientsAll ? "Everyone" : "List"}</h3>
              <span className="mx-6">or</span>
              {recipientsAll ? (
                <>
                  <button
                    type="button"
                    className="bg-neutral-600 text-white text-xs rounded px-5 py-2"
                    onClick={handleToggleRecipientsAll}
                  >
                    Send to list
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="bg-neutral-600 text-white text-xs rounded px-5 py-2"
                  onClick={handleToggleRecipientsAll}
                >
                  Send to Everyone
                </button>
              )}
            </div>
          </div>
          {recipientsAll ? (
            <div className="flex flex-row mt-4">
              <p className="text-xs mt-2">
                Send to everyone is currently unsupported for send email
              </p>
              <div className="flex flex-1 flex-col mr-10"></div>
            </div>
          ) : (
            <div className="flex flex-row mt-4">
              <div className="flex flex-1 flex-col">
                <label className="text-sm pb-2">
                  Recipients (CSV file with heading titled "unikey" and/or
                  "uos"). <br />
                  Note: This UI has been designed to support approx 20k entries.
                </label>
                <input
                  type="file"
                  id="fileRecipientsCSV"
                  name="fileRecipientsCSV"
                  onChange={handleRecipientsCSVFileChange}
                  accept=".csv"
                />
                {csvError && (
                  <p className="text-red-500 text-xs mt-2">
                    CSV file must contain at least one valid email address.
                  </p>
                )}
                <div className="mt-4">
                  <h4 className="text-sm font-semibold">Delivery Medium:</h4>
                  <div>
                    <label>
                      <input
                        type="checkbox"
                        checked={pushNotification}
                        onChange={handlePushNotificationChange}
                        className="mr-2"
                      />
                      Push Notification
                    </label>
                  </div>
                  <div className="mt-2">
                    <label>
                      <input
                        type="checkbox"
                        checked={emailNotification}
                        onChange={handleEmailNotificationChange}
                        className="mr-2"
                      />
                      Email
                    </label>
                  </div>
                  <div className="text-sm">
                    <label>
                      (ONLY @sydney.edu.au emails are currently supported)
                    </label>
                  </div>

                  {mediumError && (
                    <p className="text-red-500 text-xs mt-2">
                      At least one delivery medium must be selected.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-1 flex-col items-start pt-8">
            <button
              type="submit"
              className="bg-[#e64626] text-white text-xs rounded px-5 py-2"
            >
              Submit your notification request
            </button>
          </div>
        </form>
      }
    </Layout>
  );
};

export default CreateScreen;
